/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";

import MDBox from "components/MDBox";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";

import Footer from "components/Footer";
import {
  updateServiceEvent,
  getServiceEvents,
  removeServiceEvent,
} from "store/actions/servicesActions";
import { newAlertMessage } from "store/actions/globalAlertActions";
import serviceUtils from "../project/components/servicesConfig/serviceUtils";
import ServiceEventCalendar from "./ServiceEventsCalendar";
import ServiceEventForm from "./ServiceEventForm";

function NewServiceEvent({
  actNewAlertMessage,
  actAddServiceEvent,
  actGetServiceEvents,
  actRemoveServiceEvent,
  project,
  projectId,
  service,
  serviceId,
  apartmentId = null,
  serviceEvents,
  mainUserId,
  isProjectManager,
  clientReservations,
}) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [event, setEvent] = useState({});
  const [newEvent, setNewEvent] = useState({});
  const [actionType, setActionType] = useState(false);

  const location = useLocation();

  const calendarInitialDate = useMemo(() => {
    const initialDate = location?.state?.initialDate;

    return serviceUtils.getCalendarInitialDate(initialDate);
  }, [location?.state?.initialDate]);

  useEffect(() => {
    if (!mainUserId) return;
    if (serviceEvents?.[serviceId]) return;

    actGetServiceEvents();
  }, [mainUserId, serviceId]);

  useEffect(() => {
    setEvents(serviceUtils.getEventsByResourceId(serviceEvents, serviceId));
  }, [serviceEvents[serviceId], serviceId]);

  const handleCloseModal = useCallback(() => {
    setOpen(false);
    setEvent({});
    setNewEvent({});
    setActionType(false);
  }, []);
  const handleEventClick = (clickInfo) => {
    const eventToSelect = clickInfo?.event?.toPlainObject();
    const eventToSelectClientReservationId =
      eventToSelect?.extendedProps?.clientReservationId || eventToSelect?.clientReservationId || "";

    const selectedClientReservation = (clientReservations || []).find(
      (reservation) => reservation?.uid === eventToSelectClientReservationId
    );

    if (isProjectManager && !selectedClientReservation) {
      actNewAlertMessage({
        type: "warning",
        title: "service",
        content: "projectManagerUnauthorizedEventEdit",
      });
      return;
    }

    setNewEvent({});
    setActionType("edit");
    setEvent(eventToSelect);
    setOpen(true);
  };

  const handleDateSelect = (selectInfo) => {
    const scheduleValidationError = serviceUtils.isEventInDateRangeByResource({
      start: selectInfo?.start,
      end: new Date(
        new Date(selectInfo?.start).getTime() + Number(service?.serviceDuration || 0) * 60000
      ),
      durationMinutes: Number(service?.serviceDuration),
      resource: service,
    });

    if (scheduleValidationError === "outside_allowed_hours") {
      actNewAlertMessage({
        type: "warning",
        title: "service",
        content: "outside_allowed_hours",
      });
      return;
    }

    const selectedWeekDay = new Date(selectInfo?.start).getDay();

    if (!service?.availableDays?.includes(selectedWeekDay)) {
      actNewAlertMessage({
        type: "warning",
        title: "service",
        content: "day_not_allowed",
      });
      return;
    }

    setEvent({});
    setActionType("add");
    setNewEvent(selectInfo);
    setOpen(true);
  };

  const handleFormSubmit = useCallback(
    (data, submittedActionType) => {
      const isEditAssociatedEvent =
        submittedActionType === "edit" &&
        Boolean(data?.isAssociated || event?.extendedProps?.isAssociated || event?.isAssociated);

      const scheduleValidationError = serviceUtils.isEventInDateRangeByResource({
        start: data?.start,
        end: data?.end,
        durationMinutes: isEditAssociatedEvent ? undefined : Number(service?.serviceDuration),
        resource: service,
      });

      if (scheduleValidationError) {
        actNewAlertMessage({
          type: "warning",
          title: "service",
          content: scheduleValidationError,
        });
        return;
      }

      const isOverLimitResourceService = serviceUtils.validateServiceEventLimit(
        data,
        events,
        service
      );

      if (isOverLimitResourceService) {
        actNewAlertMessage({
          type: "warning",
          title: "service",
          content: "resourceLimitReached",
        });
        return;
      }

      actAddServiceEvent({ event: data, projectId, mainUserId }, (response) => {
        // console.log("NewServiceEvent actAddServiceEvent response", {
        //   response,
        //   actionType: submittedActionType,
        // });
        if (response?.ok) {
          actNewAlertMessage({
            type: "success",
            title: "eventService",
            content: submittedActionType === "edit" ? "eventUpdated" : "eventAdded",
          });
        } else {
          actNewAlertMessage({
            type: "error",
            title: "eventService",
            content: submittedActionType === "edit" ? "eventUpdateFailed" : "eventAddFailed",
          });
        }
      });

      handleCloseModal();
    },
    [
      actAddServiceEvent,
      actNewAlertMessage,
      events,
      handleCloseModal,
      mainUserId,
      projectId,
      service,
    ]
  );

  const handleFormRemove = useCallback(
    (ev) => {
      const clientUserId = ev?.extendedProps?.clientUserId;
      const clientReservationId =
        ev?.extendedProps?.clientReservationId || ev?.clientReservationId || "";
      const eventApartmentId = ev?.extendedProps?.apartmentId || ev?.apartmentId || apartmentId;

      const dataToSend = {
        serviceId,
        eventId: ev?.id,
        mainUserId,
        apartmentId: eventApartmentId,
        projectId,
        clientUserId,
        clientReservationId,
      };

      actRemoveServiceEvent(dataToSend, (response) => {
        if (response?.ok) {
          actNewAlertMessage({
            type: "success",
            title: "eventService",
            content: "eventRemoved",
          });
        } else {
          actNewAlertMessage({
            type: "error",
            title: "eventService",
            content: "eventRemoveFailed",
          });
        }
      });

      handleCloseModal();
    },
    [actRemoveServiceEvent, apartmentId, handleCloseModal, mainUserId, projectId, serviceId]
  );

  return (
    <DashboardLayout>
      <Modal open={open} onClose={handleCloseModal} style={{ overflow: "scroll" }}>
        <MDBox
          mt={3}
          sx={12}
          sm={8}
          style={{
            position: "absolute",
            width: "65vw",
            left: "50%",
            transform: "translate(-50%, 0%)",
          }}
        >
          <ServiceEventForm
            service={service}
            event={event}
            newEvent={newEvent}
            actionType={actionType}
            projectId={projectId}
            onSubmit={handleFormSubmit}
            onRemove={handleFormRemove}
          />
        </MDBox>
      </Modal>

      <DashboardNavbar
        title={service?.name}
        route={`/projects/${project?.name}/${service?.name}`}
        firstLink="/projects/"
        secondLink={`/projects/${projectId}`}
      />

      <MDBox pt={6} pb={3}>
        <ServiceEventCalendar
          handleDateSelect={handleDateSelect}
          handleEventClick={handleEventClick}
          events={events}
          service={service}
          initialDate={calendarInitialDate}
        />
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

NewServiceEvent.defaultProps = {
  service: {},
  projectId: "",
  serviceId: "",
  project: { name: "" },
  apartmentId: "",
  serviceEvents: {},
  mainUserId: "",
  isProjectManager: false,
  clientReservations: [],
};

NewServiceEvent.propTypes = {
  actNewAlertMessage: PropTypes.func.isRequired,
  actAddServiceEvent: PropTypes.func.isRequired,
  actGetServiceEvents: PropTypes.func.isRequired,
  actRemoveServiceEvent: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  service: PropTypes.object,
  serviceId: PropTypes.string,
  project: PropTypes.object,
  apartmentId: PropTypes.string,
  serviceEvents: PropTypes.object,
  mainUserId: PropTypes.string,
  isProjectManager: PropTypes.bool,
  clientReservations: PropTypes.array,
};

const mapStateToProps = (state, { uid, projectId, apartmentId = null }) => {
  const {
    configurationReducer,
    servicesReducer,
    projectsReducer,
    mainUserReducer,
    clientReservationsReducer,
  } = state;
  const { services, serviceEvents } = servicesReducer;
  const { isProjectManager, projects: projectManagerProjects } = mainUserReducer || {};

  const clientReservationsObj = clientReservationsReducer?.clientReservations || {};

  let clientReservations = Object.values(clientReservationsObj).flatMap((item) =>
    item && typeof item === "object" ? Object.values(item) : []
  );

  if (isProjectManager) {
    clientReservations = clientReservations.filter(
      (reservation) => projectManagerProjects?.[reservation?.projectId]
    );
  }

  return {
    apartments: configurationReducer,
    service: services[uid],
    serviceId: uid,
    projectId,
    project: projectsReducer[projectId],
    apartmentId,
    serviceEvents,
    mainUserId: mainUserReducer?.uid,
    isProjectManager,
    clientReservations,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actNewAlertMessage: (data) => dispatch(newAlertMessage(data)),
  actAddServiceEvent: (dataToSend, cb) => dispatch(updateServiceEvent({ dataToSend, cb })),
  actGetServiceEvents: () => dispatch(getServiceEvents()),
  actRemoveServiceEvent: (dataToSend, cb) => dispatch(removeServiceEvent({ dataToSend, cb })),
});

const ProjectWithConnect = connect(mapStateToProps, mapDispatchToProps)(NewServiceEvent);

function ProjectWrapper(props) {
  const { uid, projectId, apartmentId } = useParams();
  if (apartmentId)
    return (
      <ProjectWithConnect uid={uid} projectId={projectId} apartmentId={apartmentId} {...props} />
    );

  return <ProjectWithConnect uid={uid} projectId={projectId} {...props} />;
}

export default ProjectWrapper;
